import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { GoogleMap, InfoWindow, Marker, useJsApiLoader } from "@react-google-maps/api";

const FALLBACK_CENTER = { lat: 37.7749, lng: -122.4194 };

/** CI/local parity: prefer canonical name; accept legacy `REACT_APP_MAPS_API_KEY` used in older workflows. */
function resolveGoogleMapsApiKey() {
  return (process.env.REACT_APP_GOOGLE_MAPS_API_KEY || process.env.REACT_APP_MAPS_API_KEY || "").trim();
}

const GOOGLE_MAP_LIBS = [];

/** Rich night base for Google tiles — roads read clearly, water recedes */
const FIELD_MAP_DARK_STYLES = [
  { elementType: "geometry", stylers: [{ color: "#152238" }] },
  { elementType: "labels.text.stroke", stylers: [{ color: "#0a0e14" }, { weight: 2 }] },
  { elementType: "labels.text.fill", stylers: [{ color: "#b0b8c4" }] },
  { featureType: "water", elementType: "geometry", stylers: [{ color: "#0a1628" }] },
  { featureType: "road", elementType: "geometry", stylers: [{ color: "#2c3d5c" }] },
  { featureType: "road.highway", elementType: "geometry", stylers: [{ color: "#3f5580" }] },
  { featureType: "road.local", elementType: "geometry", stylers: [{ color: "#243350" }] },
  { featureType: "transit", stylers: [{ visibility: "off" }] },
  { featureType: "poi", stylers: [{ visibility: "off" }] },
];

export function formatResponderPacketId(packet) {
  const base = String(packet.id);
  if (base.toLowerCase().startsWith("demo-")) {
    const letter = packet.urgency === "RED" ? "R" : packet.urgency === "YELLOW" ? "Y" : "G";
    return `#DEMO-${letter}`;
  }
  return `#${base.slice(0, 8).toUpperCase()}`;
}

export function priorityHeading(packet) {
  if (packet.urgency === "RED") return "CRITICAL (PRIORITY 1):";
  if (packet.urgency === "YELLOW") return "URGENT (PRIORITY 2):";
  return "STABLE (PRIORITY 3):";
}

export function signalsLine(packet) {
  const raw = packet.signals || "—";
  return String(raw).replace(/·/g, "-").toUpperCase();
}

export function sourceLine(packet) {
  if (packet.isAutoGenerated) return "AUTO-SOS (passive)";
  return "MANUAL / APP";
}

function escHtml(t) {
  return String(t ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/"/g, "&quot;");
}

function leafletPopupUrgencyColor(urgency) {
  if (urgency === "RED") return "#EA4335";
  if (urgency === "YELLOW") return "#FBBC04";
  return "#34A853";
}

function leafletPopupHtml(pkt) {
  const urgency = escHtml(pkt.urgency || "GREEN");
  const injury = escHtml(pkt.injury || "Unknown");
  const lat = pkt.lat ?? 0;
  const lng = pkt.lng ?? pkt.lon ?? 0;
  const confidence = Number(pkt.confidence ?? 0);
  const color = leafletPopupUrgencyColor(pkt.urgency);
  const modeLabel = pkt.isAutoGenerated ? "AUTO-SOS" : "MANUAL";

  return `<div style='font-family:monospace;min-width:200px'>
  <div style='color:${color};font-weight:700;font-size:12px;letter-spacing:1px'>${urgency}</div>
  <div style='color:#e8eaed;font-size:14px;margin:4px 0'>${injury}</div>
  <div style='color:#9aa0a6;font-size:11px'>GPS ${lat.toFixed(4)}, ${lng.toFixed(4)}</div>
  <div style='color:#9aa0a6;font-size:11px'>Conf ${(confidence * 100).toFixed(0)}% · ${modeLabel}</div>
</div>`;
}

function pinPalette(urgency) {
  if (urgency === "RED") {
    return { fill: "#EA4335", light: "#FF6659", dark: "#B31412", stroke: "#FFCDD2", letter: "R" };
  }
  if (urgency === "YELLOW") {
    return { fill: "#F9AB00", light: "#FFD567", dark: "#C08100", stroke: "#FFF8E1", letter: "Y" };
  }
  return { fill: "#34A853", light: "#81C995", dark: "#0F9D58", stroke: "#E8F5E9", letter: "G" };
}

/** SVG pins: triage flag ribbon + gradient teardrop + medical cross. Unique gradient IDs per instance. */
function makePinIconDataUrl(urgency, selected) {
  const p = pinPalette(urgency);
  const uid = `g${Math.random().toString(36).slice(2, 10)}`;
  const halo = selected ? p.stroke : "rgba(255,255,255,0.9)";
  const haloWidth = selected ? 2.8 : 2.1;
  const svg = `
<svg xmlns="http://www.w3.org/2000/svg" width="46" height="56" viewBox="0 0 64 78" aria-hidden="true">
  <defs>
    <linearGradient id="${uid}-grad" x1="0%" y1="0%" x2="0%" y2="100%">
      <stop offset="0%" stop-color="${p.light}"/>
      <stop offset="52%" stop-color="${p.fill}"/>
      <stop offset="100%" stop-color="${p.dark}"/>
    </linearGradient>
    <filter id="${uid}-sh" x="-45%" y="-45%" width="190%" height="190%">
      <feDropShadow dx="0" dy="2" stdDeviation="2.2" flood-color="#000" flood-opacity="0.48"/>
    </filter>
  </defs>
  <path fill="${p.dark}" stroke="rgba(0,0,0,0.35)" stroke-width="0.65"
    d="M21 3 L43 3 L41 15 L32 20 L23 15 Z"/>
  <text x="32" y="13.5" text-anchor="middle" fill="#fff" font-size="8.5" font-family="system-ui,sans-serif" font-weight="800">${p.letter}</text>
  <path filter="url(#${uid}-sh)" fill="url(#${uid}-grad)" stroke="${halo}" stroke-width="${haloWidth}"
    d="M32 14 C17.8 14 7 23.8 7 36.5 c0 15 17.5 34 23.2 39.4 a2.8 2.8 0 004.1 0 C40.3 70.5 57 51.5 57 36.5 C57 23.8 46.2 14 32 14z"/>
  <ellipse cx="32" cy="38" rx="13" ry="13" fill="rgba(255,255,255,0.14)"/>
  <path fill="#fff" d="M32 29.2c-.55 0-1 .45-1 1v5.6h-5.6c-.55 0-1 .45-1 1s.45 1 1 1h5.6v5.6c0 .55.45 1 1 1s1-.45 1-1v-5.6h5.6c.55 0 1-.45 1-1s-.45-1-1-1h-5.6v-5.6c0-.55-.45-1-1-1z"/>
</svg>`;
  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg.trim())}`;
}

let leafletLoadPromise = null;

function loadLeafletHeatPlugin() {
  return new Promise((resolve, reject) => {
    if (typeof window === "undefined") return resolve();
    if (window.L?.heatLayer) return resolve();
    const script = document.createElement("script");
    script.src = "https://unpkg.com/leaflet.heat@0.2.0/dist/leaflet-heat.js";
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("leaflet.heat failed to load"));
    document.head.appendChild(script);
  });
}

function loadLeafletAssets() {
  if (typeof window === "undefined") return Promise.resolve();
  if (window.L?.heatLayer) return Promise.resolve();
  if (leafletLoadPromise) return leafletLoadPromise;

  leafletLoadPromise = new Promise((resolve, reject) => {
    const finish = () => loadLeafletHeatPlugin().then(resolve).catch(reject);

    if (window.L) {
      finish();
      return;
    }

    const css = document.createElement("link");
    css.rel = "stylesheet";
    css.href = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.css";
    document.head.appendChild(css);

    const script = document.createElement("script");
    script.src = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.js";
    script.async = true;
    script.onload = () => finish();
    script.onerror = () => reject(new Error("Leaflet failed to load"));
    document.head.appendChild(script);
  });

  return leafletLoadPromise;
}

function leafletPinIcon(L, urgency, selected) {
  const p = pinPalette(urgency);
  const uid = `l${Math.random().toString(36).slice(2, 10)}`;
  const halo = selected ? p.stroke : "rgba(255,255,255,0.9)";
  const haloWidth = selected ? 2.8 : 2.1;
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="46" height="56" viewBox="0 0 64 78" aria-hidden="true">
  <defs>
    <linearGradient id="${uid}-grad" x1="0%" y1="0%" x2="0%" y2="100%">
      <stop offset="0%" stop-color="${p.light}"/>
      <stop offset="52%" stop-color="${p.fill}"/>
      <stop offset="100%" stop-color="${p.dark}"/>
    </linearGradient>
    <filter id="${uid}-sh" x="-45%" y="-45%" width="190%" height="190%">
      <feDropShadow dx="0" dy="2" stdDeviation="2.2" flood-color="#000" flood-opacity="0.48"/>
    </filter>
  </defs>
  <path fill="${p.dark}" stroke="rgba(0,0,0,0.35)" stroke-width="0.65"
    d="M21 3 L43 3 L41 15 L32 20 L23 15 Z"/>
  <text x="32" y="13.5" text-anchor="middle" fill="#fff" font-size="8.5" font-family="system-ui,sans-serif" font-weight="800">${p.letter}</text>
  <path filter="url(#${uid}-sh)" fill="url(#${uid}-grad)" stroke="${halo}" stroke-width="${haloWidth}"
    d="M32 14 C17.8 14 7 23.8 7 36.5 c0 15 17.5 34 23.2 39.4 a2.8 2.8 0 004.1 0 C40.3 70.5 57 51.5 57 36.5 C57 23.8 46.2 14 32 14z"/>
  <ellipse cx="32" cy="38" rx="13" ry="13" fill="rgba(255,255,255,0.14)"/>
  <path fill="#fff" d="M32 29.2c-.55 0-1 .45-1 1v5.6h-5.6c-.55 0-1 .45-1 1s.45 1 1 1h5.6v5.6c0 .55.45 1 1 1s1-.45 1-1v-5.6h5.6c.55 0 1-.45 1-1s-.45-1-1-1h-5.6v-5.6c0-.55-.45-1-1-1z"/>
</svg>`;
  const wrapClass = `omni-field-pin-wrap${selected ? " is-selected" : ""}`;
  return L.divIcon({
    html: `<div class="${wrapClass}">${svg}</div>`,
    className: "omni-field-marker",
    iconSize: [46, 56],
    iconAnchor: [23, 56],
    popupAnchor: [0, -48],
  });
}

function FieldMapInfoBody({ packet }) {
  const lat = packet.lat;
  const lng = packet.lng ?? packet.lon;
  const mapsUrl =
    lat != null && lng != null
      ? `https://www.google.com/maps?q=${lat},${lng}&z=17`
      : null;
  const u = (packet.urgency || "GREEN").toLowerCase();

  return (
    <div className={`field-map-infowindow-inner field-map-infowindow--${u}`}>
      <div className={`field-map-infowindow-urgency field-map-iw-${u}`}>
        {packet.urgency} · {priorityHeading(packet).replace(/:$/, "")}
      </div>
      <div className="field-map-infowindow-id">{formatResponderPacketId(packet)}</div>
      <p className="field-map-infowindow-injury">{packet.injury || "Unknown"}</p>
      <dl className="field-map-infowindow-dl">
        <div>
          <dt>Location</dt>
          <dd>
            {lat != null && lng != null ? (
              <>
                {lat.toFixed(5)}°, {lng.toFixed(5)}°
                {mapsUrl ? (
                  <>
                    {" · "}
                    <a href={mapsUrl} target="_blank" rel="noopener noreferrer">
                      Open in Maps
                    </a>
                  </>
                ) : null}
              </>
            ) : (
              "—"
            )}
          </dd>
        </div>
        <div>
          <dt>Signals</dt>
          <dd>{signalsLine(packet)}</dd>
        </div>
        <div>
          <dt>Source</dt>
          <dd>{sourceLine(packet)}</dd>
        </div>
      </dl>
    </div>
  );
}

const HEAT_LAYER_OPTS = {
  radius: 35,
  blur: 25,
  maxZoom: 17,
  gradient: { 0.4: "#FBBC04", 0.65: "#EA4335", 1: "#A50E0E" },
};

function runSonarSweep(map) {
  const L = typeof window !== "undefined" ? window.L : null;
  if (!map || !L) return;
  const center = map.getCenter();
  const circle = L.circle(center, {
    radius: 50,
    color: "#34A853",
    weight: 2,
    fillOpacity: 0,
    opacity: 0.6,
  }).addTo(map);

  const durationMs = 3000;
  const start = typeof performance !== "undefined" ? performance.now() : Date.now();

  function frame(now) {
    const t = Math.min(1, (now - start) / durationMs);
    const radius = 50 + (500 - 50) * t;
    const opacity = 0.6 * (1 - t);
    circle.setRadius(radius);
    circle.setStyle({ opacity, fillOpacity: 0 });
    if (t < 1) {
      requestAnimationFrame(frame);
    } else {
      map.removeLayer(circle);
    }
  }
  requestAnimationFrame(frame);
}

function LeafletFieldMap({ packets, selectedId, setSelectedId }) {
  const mapElRef = useRef(null);
  const mapRef = useRef(null);
  const markersByIdRef = useRef({});
  const resizeHandlerRef = useRef(null);
  const heatLayerRef = useRef(null);
  const sonarIntervalRef = useRef(null);
  const [mapReady, setMapReady] = useState(false);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        await loadLeafletAssets();
      } catch {
        return;
      }
      if (cancelled || !mapElRef.current || mapRef.current) return;

      const L = window.L;
      const first = packets.find((p) => p.lat != null && (p.lng != null || p.lon != null));
      const center = first ? [first.lat, first.lng ?? first.lon] : [FALLBACK_CENTER.lat, FALLBACK_CENTER.lng];

      const map = L.map(mapElRef.current, { zoomControl: false }).setView(center, 14);

      L.tileLayer("https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png", {
        attribution: "© OpenStreetMap © CARTO",
        subdomains: "abcd",
        maxZoom: 20,
      }).addTo(map);

      L.control.zoom({ position: "bottomright" }).addTo(map);

      mapRef.current = map;

      map.once("load", () => {
        if (cancelled || mapRef.current !== map) return;
        runSonarSweep(map);
        sonarIntervalRef.current = window.setInterval(() => {
          runSonarSweep(mapRef.current);
        }, 4000);
      });

      setMapReady(true);

      resizeHandlerRef.current = () => map.invalidateSize();
      window.addEventListener("resize", resizeHandlerRef.current);
    })();

    return () => {
      cancelled = true;
      setMapReady(false);
      if (sonarIntervalRef.current != null) {
        window.clearInterval(sonarIntervalRef.current);
        sonarIntervalRef.current = null;
      }
      heatLayerRef.current = null;
      if (resizeHandlerRef.current) {
        window.removeEventListener("resize", resizeHandlerRef.current);
        resizeHandlerRef.current = null;
      }
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    const el = mapElRef.current;
    if (!mapReady || !map || !el || typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver(() => {
      map.invalidateSize({ animate: false });
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [mapReady]);

  useEffect(() => {
    const map = mapRef.current;
    const L = typeof window !== "undefined" ? window.L : null;
    if (!mapReady || !map || !L) return undefined;

    Object.values(markersByIdRef.current).forEach((m) => {
      try {
        m.remove();
      } catch {
        /* ignore */
      }
    });
    markersByIdRef.current = {};

    packets.forEach((pkt) => {
      const lat = pkt.lat;
      const lng = pkt.lng ?? pkt.lon;
      if (lat == null || lng == null) return;

      const selected = pkt.id === selectedId;
      const marker = L.marker([lat, lng], {
        icon: leafletPinIcon(L, pkt.urgency, selected),
      }).addTo(map);

      marker.bindPopup(leafletPopupHtml(pkt), {
        maxWidth: 320,
        className: "dark-popup",
      });

      marker.on("click", () => {
        setSelectedId(pkt.id);
      });

      markersByIdRef.current[pkt.id] = marker;

      requestAnimationFrame(() => {
        const iconEl =
          typeof marker.getElement === "function" ? marker.getElement() : marker._icon;
        if (iconEl && pkt.urgency === "RED") iconEl.classList.add("pulsing-marker");
      });
    });

    if (typeof L.heatLayer === "function") {
      if (!heatLayerRef.current) {
        heatLayerRef.current = L.heatLayer([], HEAT_LAYER_OPTS).addTo(map);
      }
      const redHeat = packets
        .filter((p) => p.urgency === "RED" && p.lat != null && (p.lng ?? p.lon) != null)
        .map((p) => [p.lat, p.lng ?? p.lon, 1.0]);
      heatLayerRef.current.setLatLngs(redHeat);
    }

    const sel = packets.find((p) => p.id === selectedId);
    if (sel && sel.lat != null && (sel.lng ?? sel.lon) != null) {
      map.setView([sel.lat, sel.lng ?? sel.lon], Math.max(map.getZoom(), 16), { animate: true });
      const mk = markersByIdRef.current[sel.id];
      if (mk) mk.openPopup();
    } else if (packets.length > 0) {
      const valid = packets.filter((p) => p.lat != null && (p.lng ?? p.lon) != null);
      if (valid.length > 0) {
        const b = L.latLngBounds(valid.map((p) => [p.lat, p.lng ?? p.lon]));
        map.fitBounds(b.pad(0.15));
      }
    }

    map.invalidateSize();

    return undefined;
  }, [packets, selectedId, setSelectedId, mapReady]);

  return (
    <div className="field-map-stack field-map-tiles-voyager">
      <div ref={mapElRef} className="responder-map-leaflet" />
    </div>
  );
}

function GoogleFieldMap({ packets, selectedId, setSelectedId }) {
  const apiKey = resolveGoogleMapsApiKey();
  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: apiKey,
    libraries: GOOGLE_MAP_LIBS,
  });

  const mapRef = useRef(null);
  const validPackets = useMemo(
    () => packets.filter((p) => p.lat != null && (p.lng != null || p.lon != null)),
    [packets],
  );

  const center = useMemo(() => {
    if (validPackets.length === 0) return FALLBACK_CENTER;
    const lat = validPackets.reduce((s, p) => s + p.lat, 0) / validPackets.length;
    const lng = validPackets.reduce((s, p) => s + (p.lng ?? p.lon), 0) / validPackets.length;
    return { lat, lng };
  }, [validPackets]);

  const selectedPacket = useMemo(
    () => packets.find((p) => p.id === selectedId && p.lat != null && (p.lng ?? p.lon) != null),
    [packets, selectedId],
  );

  const onLoad = useCallback((map) => {
    mapRef.current = map;
  }, []);

  const onUnmount = useCallback(() => {
    mapRef.current = null;
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !isLoaded || !window.google?.maps) return;

    if (selectedPacket) {
      const lng = selectedPacket.lng ?? selectedPacket.lon;
      map.panTo({ lat: selectedPacket.lat, lng });
      map.setZoom(Math.max(map.getZoom() || 14, 16));
      return;
    }

    if (validPackets.length > 0) {
      const bounds = new window.google.maps.LatLngBounds();
      validPackets.forEach((p) => bounds.extend({ lat: p.lat, lng: p.lng ?? p.lon }));
      map.fitBounds(bounds, 48);
    }
  }, [isLoaded, selectedPacket, validPackets]);

  if (loadError) {
    return (
      <div className="field-map-error">
        Google Maps failed to load. Enable <strong>Maps JavaScript API</strong> and verify{" "}
        <code className="field-map-code">REACT_APP_GOOGLE_MAPS_API_KEY</code>.
      </div>
    );
  }

  if (!isLoaded) {
    return <div className="field-map-loading">Loading Google Maps…</div>;
  }

  return (
    <GoogleMap
      mapContainerClassName="responder-map-leaflet responder-map-gmaps"
      center={center}
      zoom={validPackets.length ? 14 : 12}
      onLoad={onLoad}
      onUnmount={onUnmount}
      options={{
        disableDefaultUI: false,
        zoomControl: true,
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: false,
        styles: FIELD_MAP_DARK_STYLES,
      }}
    >
      {validPackets.map((pkt) => {
        const lng = pkt.lng ?? pkt.lon;
        const selected = pkt.id === selectedId;
        const iconUrl = makePinIconDataUrl(pkt.urgency, selected);
        return (
          <Marker
            key={pkt.id}
            position={{ lat: pkt.lat, lng }}
            icon={{
              url: iconUrl,
              scaledSize: new window.google.maps.Size(38, 46),
              anchor: new window.google.maps.Point(19, 46),
            }}
            onClick={() => setSelectedId(pkt.id)}
            zIndex={selected ? 1000 : pkt.urgency === "RED" ? 100 : 10}
          />
        );
      })}

      {selectedPacket ? (
        <InfoWindow
          position={{
            lat: selectedPacket.lat,
            lng: selectedPacket.lng ?? selectedPacket.lon,
          }}
          onCloseClick={() => setSelectedId(null)}
          options={{ maxWidth: 320, pixelOffset: new window.google.maps.Size(0, -8) }}
        >
          <FieldMapInfoBody packet={selectedPacket} />
        </InfoWindow>
      ) : null}
    </GoogleMap>
  );
}

/**
 * Field responder map: Google Maps when REACT_APP_GOOGLE_MAPS_API_KEY or legacy REACT_APP_MAPS_API_KEY is set,
 * otherwise Leaflet + Carto dark with the same pins and selection behaviour.
 */
export default function FieldMap({ packets, selectedId, setSelectedId }) {
  const apiKey = resolveGoogleMapsApiKey();
  const useGoogle = apiKey.length >= 20;

  return (
    <div className="field-map-root">
      {useGoogle ? (
        <GoogleFieldMap packets={packets} selectedId={selectedId} setSelectedId={setSelectedId} />
      ) : (
        <LeafletFieldMap packets={packets} selectedId={selectedId} setSelectedId={setSelectedId} />
      )}
    </div>
  );
}
